<template>
    <div class="u-border u-border-top-none mb-12">
        <Notification v-if="form.error" :notification="form.error" status="error"/>

        <div  v-for="(item, index) in puzzle.words" class="flex w-full mb-16" :class="item.is_bonus ? 'border-t pt-16' : null" :key="index">
              <div class="flex flex-col items-center justify-center w-64 h-40 mr-16" :style="`backgroundColor: ${item.color}`">
                  <p class="font-semibold">{{ getLetter(item, index) }} ({{ item.cells.length }})</p>
              </div>

              <div class="flex flex-col flex-1">
                <FieldInput
                        class="flex-1 mb-4"
                        v-if="formFields[`word_${index}`]"
                        :fieldId="formFields[`word_${index}`].name"
                        :placeholder="item.is_bonus ? 'Restwoord' : formFields[`word_${index}`].label"
                        :value="formFields[`word_${index}`].value"
                        :required="formFields[`word_${index}`].required"
                        :name="formFields[`word_${index}`].name"
                        :type="formFields[`word_${index}`].type"
                        :rule="formFields[`word_${index}`].rule"
                        :module="module"
                        :subform="subform"
                        :error="formFields[`word_${index}`].error"
                        :minlength="item.cells.length"
                        :maxlength="item.cells.length"
                        :formError="formError"
                        @change="updatePuzzleWordObject(index, 'word', $event)"
                        @focus="setActiveWordIndex(index)"
                        @blur="setActiveWordIndex(null)"/>

            
                <FieldInput
                        class="flex-1 mb-4"
                        v-if="formFields[`tip_${index}`]"
                        :fieldId="formFields[`tip_${index}`].name"
                        :value="formFields[`tip_${index}`].value"
                        :required="formFields[`tip_${index}`].required"
                        :name="formFields[`tip_${index}`].name"
                        :placeholder="formFields[`tip_${index}`].placeholder"
                        :type="formFields[`tip_${index}`].type"
                        :rule="formFields[`tip_${index}`].rule"
                        :module="module"
                        :subform="subform"
                        :error="formFields[`tip_${index}`].error"
                        :formError="formError"
                        @change="updatePuzzleWordObject(index, 'tip', $event)"
                />

                <FieldInput
                        class="flex-1 mb-4"
                        v-if="formFields[`href_${index}`]"
                        :fieldId="formFields[`href_${index}`].name"
                        :value="formFields[`href_${index}`].value"
                        :required="formFields[`href_${index}`].required"
                        :name="formFields[`href_${index}`].name"
                        :placeholder="formFields[`href_${index}`].placeholder"
                        :type="formFields[`href_${index}`].type"
                        :rule="formFields[`href_${index}`].rule"
                        :module="module"
                        :subform="subform"
                        :error="formFields[`href_${index}`].error"
                        :formError="formError"
                        @change="updatePuzzleWordObject(index, 'href', $event)"
                />

                  <template v-if="formFields[`href_${index}`]">
                      <div v-if="formFields[`opengraph_data_${index}`].value" class="mb-4">
                          <strong>Artikel voorbeeld:</strong>
                          <PuzzleArticleListItem
                              :opengraphData="formFields[`opengraph_data_${index}`].value"
                              class="full-width"
                          />
                      </div>

                      <button
                              v-if="formFields[`href_${index}`].value"
                              @click="fetchOpengraphData(index)"
                              class="c-button mb-4"
                              type="button"
                      >
                          Artikel data <template v-if="formFields[`opengraph_data_${index}`].value">opnieuw</template> ophalen
                      </button>
                  </template>
              </div>

            </div>
        </div>

</template>

<script>
  import { mapState } from 'vuex'
  import { clone, cloneDeep, filter } from 'lodash'

  import Notification from '@/components/Notification'
  import FieldInput from '@/components/forms/FieldInput'
  import FieldCheckbox from '@/components/forms/FieldCheckbox'
  import PuzzleArticleListItem from '@/components/puzzle/PuzzleArticleListItem'

  export default {
    name: 'AdminPuzzleWordsForm',

    data () {
      return {
        module: 'admin',
        subform: 'puzzleWordsForm'
      }
    },

    created () {
      this.setFormFields()
    },

    methods: {
      setFormFields () {
        this.$store.commit('admin/resetPuzzleWordsForm')

        const templateFields = this.templateFields

        this.puzzle.words.forEach((wordObject, index) => {
          let fields = templateFields

          if (wordObject.is_bonus) {
            fields = filter(fields, item => item.name !== 'href')
          }

          fields.forEach((field) => {
            field = cloneDeep(field)
            field.value = wordObject[field.name]

            if (field.name === 'word') {
              field.rule.push(`size:${wordObject.cells.length}`)
            }

            field.name = `${field.name}_${index}`
            this.$store.commit('addField', { module: this.module, subform: this.subform, field })
          })
        })
      },

      updatePuzzleWordObject (index, property, value) {
        const word = clone(this.puzzle.words[index])
        word[property] = value

        if (property === 'word') {
            let cells = cloneDeep(word.cells)

            for (let charIndex = 0; charIndex < cells.length; charIndex++) {
                cells[charIndex].char = value[charIndex] ?? ''
            }

            word.cells = cells
        }

        this.$store.commit('setPuzzleWord', { word, index })
      },

      setActiveWordIndex (activeWordIndex) {
        this.$store.commit('setActiveWordIndex', { activeWordIndex })
      },

      getLetter (word, index) {
        return word.is_bonus ? 'X' : this.$options.filters.numberToLetter(index)
      },

      fetchOpengraphData (index) {
        const href = this.formFields[`href_${index}`].value

        this.$store.dispatch('openGraphData', { href })
          .then((result) => {
            const value = JSON.stringify(result.openGraph)
            this.$store.commit('setFieldValue', {
              module: this.module,
              subform: this.subform,
              field: `opengraph_data_${index}`,
              value
            })

            this.updatePuzzleWordObject(index, 'opengraph_data', value)
          })
      }
    },

    watch: {
      puzzle: {
        handler: function (newValue) {
          this.setFormFields()
        }
      }
    },

    computed: {
      ...mapState({
        puzzle: state => state.currentPuzzle,
        gridSettings: state => state.gridSettings
      }),

      ...mapState('admin', {
        form: state => state.puzzleWordsForm.form,
        formError: state => state.puzzleWordsForm.form.error,
        formFields: state => state.puzzleWordsForm.form.fields,
        templateFields: state => state.puzzleWordsForm.form.templateFields
      })
    },

    components: {
      Notification,
      FieldInput,
      FieldCheckbox,
      PuzzleArticleListItem
    }
  }
</script>
